import { useState, useEffect, useCallback, useRef } from 'react';
import { getDefaultTenant } from '@/services/tenant';
import {
  getModelConfigList,
  ModelConfig,
  ApiModelType,
} from '@/services/admin';

// 需要配置的模型类型（仅前端可配置的模型）
const REQUIRED_MODEL_TYPES: ApiModelType[] = ['chat', 'embedding'];

// 会话存储 key
const GUIDE_DISMISSED_KEY = 'admin_model_guide_dismissed';
const GUIDE_SHOWN_KEY = 'admin_model_guide_shown';

// 缓存 key
const MODEL_CONFIG_CACHE_KEY = 'admin_model_config_cache';

// 缓存有效期 (2分钟)
const CACHE_TTL = 2 * 60 * 1000;

export interface ModelConfigStatus {
  isLoading: boolean;
  isChecked: boolean; // 是否已完成检查
  isFullyConfigured: boolean; // 是否全部配置完成
  configuredTypes: ApiModelType[]; // 已配置的类型
  missingTypes: ApiModelType[]; // 缺失的类型
  defaultTenantId: string | null; // 默认租户ID
}

export interface UseModelConfigStatusReturn extends ModelConfigStatus {
  showGuideModal: boolean;
  setShowGuideModal: (show: boolean) => void;
  refreshStatus: () => Promise<void>;
  dismissGuideForSession: () => void; // 本次会话内不再显示
}

// 模块级缓存 - 避免重复请求
interface ConfigCache {
  status: ModelConfigStatus;
  timestamp: number;
}
let configCache: ConfigCache | null = null;

const MODEL_CONFIG_STATUS_UPDATED_EVENT = 'model-config-status-updated';
const modelConfigStatusEventTarget = new EventTarget();

export function notifyModelConfigStatusUpdated() {
  modelConfigStatusEventTarget.dispatchEvent(
    new Event(MODEL_CONFIG_STATUS_UPDATED_EVENT)
  );
}

// 获取缓存
function getCache(): ModelConfigStatus | null {
  // 先检查内存缓存
  if (configCache && Date.now() - configCache.timestamp < CACHE_TTL) {
    return configCache.status;
  }

  // 再检查 sessionStorage
  try {
    const cached = sessionStorage.getItem(MODEL_CONFIG_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as ConfigCache;
      if (Date.now() - parsed.timestamp < CACHE_TTL) {
        configCache = parsed; // 同步到内存缓存
        return parsed.status;
      }
    }
  } catch {
    // 忽略解析错误
  }
  return null;
}

// 设置缓存
function setCache(status: ModelConfigStatus) {
  const cache: ConfigCache = { status, timestamp: Date.now() };
  configCache = cache;
  try {
    sessionStorage.setItem(MODEL_CONFIG_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // 忽略存储错误
  }
  notifyModelConfigStatusUpdated();
}

// 清除缓存 (导出供外部使用)
export function clearModelConfigCache() {
  configCache = null;
  try {
    sessionStorage.removeItem(MODEL_CONFIG_CACHE_KEY);
  } catch {
    // 忽略
  }
}

export function useModelConfigStatus(): UseModelConfigStatusReturn {
  // 尝试从缓存初始化
  const cachedStatus = getCache();

  const [status, setStatus] = useState<ModelConfigStatus>(
    cachedStatus || {
      isLoading: true,
      isChecked: false,
      isFullyConfigured: false,
      configuredTypes: [],
      missingTypes: REQUIRED_MODEL_TYPES,
      defaultTenantId: null,
    }
  );

  const [showGuideModal, setShowGuideModal] = useState(false);

  // 防止重复请求
  const isCheckingRef = useRef(false);

  // 检查模型配置状态
  const checkStatus = useCallback(async (forceRefresh = false) => {
    // 如果正在检查，跳过
    if (isCheckingRef.current) {
      console.log('⏳ 模型配置检查正在进行中，跳过');
      return;
    }

    // 如果有有效缓存且不是强制刷新，直接使用缓存
    if (!forceRefresh) {
      const cached = getCache();
      if (cached) {
        console.log('📦 使用缓存的模型配置状态');
        setStatus(cached);

        // 检查是否需要显示弹窗
        const isDismissed = sessionStorage.getItem(GUIDE_DISMISSED_KEY);
        const isShown = sessionStorage.getItem(GUIDE_SHOWN_KEY);
        if (!cached.isFullyConfigured && !isDismissed && !isShown) {
          sessionStorage.setItem(GUIDE_SHOWN_KEY, 'true');
          setShowGuideModal(true);
        } else if (cached.isFullyConfigured) {
          setShowGuideModal(false);
        }
        return;
      }
    }

    isCheckingRef.current = true;
    setStatus((prev) => ({ ...prev, isLoading: true }));

    try {
      console.log('🔍 检查模型配置状态...');

      // 1. 获取默认租户
      const defaultTenant = await getDefaultTenant();
      if (!defaultTenant) {
        const newStatus: ModelConfigStatus = {
          isLoading: false,
          isChecked: true,
          isFullyConfigured: false,
          configuredTypes: [],
          missingTypes: REQUIRED_MODEL_TYPES,
          defaultTenantId: null,
        };
        setStatus(newStatus);
        setCache(newStatus);
        return;
      }

      // 2. 获取模型配置列表
      const response = await getModelConfigList(defaultTenant.id);
      const configs = response.configs || [];

      // 3. 分析配置状态
      const configuredTypes = REQUIRED_MODEL_TYPES.filter((type) =>
        configs.some(
          (config: ModelConfig) => config.type === type && config.is_active
        )
      );

      const missingTypes = REQUIRED_MODEL_TYPES.filter(
        (type) => !configuredTypes.includes(type)
      );

      const isFullyConfigured = missingTypes.length === 0;

      const newStatus: ModelConfigStatus = {
        isLoading: false,
        isChecked: true,
        isFullyConfigured,
        configuredTypes,
        missingTypes,
        defaultTenantId: defaultTenant.id,
      };

      setStatus(newStatus);
      setCache(newStatus);
      console.log('✅ 模型配置检查完成');

      // 4. 如果未完全配置且本次会话未dismiss，显示弹窗
      const isDismissed = sessionStorage.getItem(GUIDE_DISMISSED_KEY);
      const isShown = sessionStorage.getItem(GUIDE_SHOWN_KEY);
      if (!isFullyConfigured && !isDismissed && !isShown) {
        sessionStorage.setItem(GUIDE_SHOWN_KEY, 'true');
        setShowGuideModal(true);
      } else if (isFullyConfigured) {
        setShowGuideModal(false);
      }
    } catch (error) {
      console.error('检查模型配置状态失败:', error);
      setStatus((prev) => ({
        ...prev,
        isLoading: false,
        isChecked: true,
      }));
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  // 刷新状态 (强制刷新)
  const refreshStatus = useCallback(async () => {
    clearModelConfigCache();
    await checkStatus(true);
  }, [checkStatus]);

  // 本次会话不再显示
  const dismissGuideForSession = useCallback(() => {
    sessionStorage.setItem(GUIDE_DISMISSED_KEY, 'true');
    setShowGuideModal(false);
  }, []);

  // 初始化时检查
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // 监听其他实例的状态更新
  useEffect(() => {
    const handleStatusUpdated = () => {
      const cached = getCache();
      if (cached) {
        setStatus(cached);
        const isDismissed = sessionStorage.getItem(GUIDE_DISMISSED_KEY);
        const isShown = sessionStorage.getItem(GUIDE_SHOWN_KEY);
        if (!cached.isFullyConfigured && !isDismissed && !isShown) {
          sessionStorage.setItem(GUIDE_SHOWN_KEY, 'true');
          setShowGuideModal(true);
        } else if (cached.isFullyConfigured) {
          setShowGuideModal(false);
        }
        return;
      }
      checkStatus(true);
    };

    modelConfigStatusEventTarget.addEventListener(
      MODEL_CONFIG_STATUS_UPDATED_EVENT,
      handleStatusUpdated
    );
    return () => {
      modelConfigStatusEventTarget.removeEventListener(
        MODEL_CONFIG_STATUS_UPDATED_EVENT,
        handleStatusUpdated
      );
    };
  }, [checkStatus]);

  return {
    ...status,
    showGuideModal,
    setShowGuideModal,
    refreshStatus,
    dismissGuideForSession,
  };
}
